import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import "./store-status-banner.css";

interface StoreStatus {
  isOpen: boolean;
  message: string | null;
  expectedReturnAt: string | null;
}

/**
 * Polls /api/store-status every 90s. Renders an informational banner above
 * main content when the admin has flipped the store to "Volta Já". Never
 * blocks any flow — purely informational.
 */
export function StoreStatusBanner() {
  const { t, i18n } = useTranslation();
  const { data } = useQuery<StoreStatus>({
    queryKey: ["store-status"],
    queryFn: () => api<StoreStatus>("/api/store-status"),
    refetchInterval: 90 * 1000,
    retry: false,
  });

  if (!data || data.isOpen) return null;

  const returnTime = data.expectedReturnAt
    ? new Intl.DateTimeFormat(i18n.language || "pt-PT", {
        timeZone: "Europe/Lisbon",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(data.expectedReturnAt))
    : null;

  return (
    <aside className="store-status-banner" role="status" aria-live="polite">
      <strong className="store-status-banner__lead">{t("admin.storeStatus.away")}</strong>
      <span className="store-status-banner__body">
        {t("admin.storeStatus.bannerClosed")}
        {returnTime ? ` — ${t("admin.storeStatus.bannerReturn", { time: returnTime })}` : ""}
        {data.message ? `. ${data.message}` : ""}.
      </span>
    </aside>
  );
}
